<template>
    <div class="ah-page project-accounting-balance-sheet">
        <div class="page-header bottom-margin">
            <div class="ah-container">
                <div class="title-row">
                    <div class="title">
                        <h1>專案資產負債表</h1>
                    </div>
                    <div class="actions">
                        <el-button @click="onPrintClick" icon="el-icon-printer">列印</el-button>
                    </div>
                </div>
            </div>
        </div>
        <div class="ah-container">
            <div class="ah-panel no-print">
                <div class="panel-body">
                    <search-form>
                        <search-form-item label="查詢日期">
                            <div class="horizontal-inputs">
                                <ahDropdownDatePicker v-model="dateRange" :showEndPicker="false" :quickButton="false" @change="updateData" />
                            </div>
                        </search-form-item>
                        <search-form-item label="專案">
                            <el-select v-model="selectedProject" placeholder="請選擇專案" value-key="poo" clearable multiple filterable reserve-keyword @change="onSelectProjectChange" style="width: 600px">
                                <el-option v-for="item in projectList" :key="item.poo" :label="item.poo + ' ' + item.nam" :value="item" style="max-width: 600px" />
                            </el-select>
                        </search-form-item>
                        <search-form-item label="金額為 0 的科目">
                            <el-radio v-model="isShowZeroItems" :label="true" @change="updateData">顯示</el-radio>
                            <el-radio v-model="isShowZeroItems" :label="false" @change="updateData">隱藏</el-radio>
                        </search-form-item>
                    </search-form>
                </div>
            </div>
            <div class="ah-panel" style="margin-top: -20px">
                <div class="panel-body" :class="{ loading: isLoading }">
                    <div class="doc-header">
                        <div class="company-name">{{ companyName }}<span v-if="isTrying">（試用）</span></div>
                        <div class="doc-name">專案資產負債表</div>
                        <div class="date">{{ queryDateText }}</div>
                        <div class="date" style="text-align: right; font-size: 8pt; margin-bottom: 10px">製表日期：{{ todayDateTxt }}</div>
                    </div>
                    <div class="table-block">
                        <div class="balance-sheet-table">
                            <div class="cell-block cell-assets-title">
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">資產</div>
                                            <div class="total">金額</div>
                                            <div class="percent">%</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-assets-items">
                                <!-- 流動資產 -->
                                <div class="group secondary" v-if="datas.currentAssets.length > 0">
                                    <div class="group-title">流動資產</div>
                                    <ul class="item-list">
                                        <li v-for="(item, index) in datas.currentAssets" :key="`${index}-${item.accno}`">
                                            <div class="accno">{{ item.accno }}</div>
                                            <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                            <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                            <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.assets) }} %</div>
                                        </li>
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">流動資產總額</div>
                                            <div class="total"><Money :v="totals.currentAssets" /></div>
                                            <div :class="{ percent: true, negative: totals.currentAssets < 0 }">{{ getPercent(totals.currentAssets, totals.assets) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                                <!-- 非流動資產 -->
                                <div class="group secondary" v-if="datas.nonCurrentAssets.length > 0">
                                    <div class="group-title" style="margin-top: 20px">非流動資產</div>
                                    <ul class="item-list">
                                        <li v-for="(item, index) in datas.nonCurrentAssets" :key="`${index}-${item.accno}`">
                                            <div class="accno">{{ item.accno }}</div>
                                            <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                            <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                            <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.assets) }} %</div>
                                        </li>
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">非流動資產總額</div>
                                            <div class="total"><Money :v="totals.nonCurrentAssets" /></div>
                                            <div :class="{ percent: true, negative: totals.nonCurrentAssets < 0 }">{{ getPercent(totals.nonCurrentAssets, totals.assets) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-assets-total">
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">資產總額</div>
                                            <div class="total" :class="{ unequal: !isAssetsAndLiabilitiesEqual }"><Money :v="totals.assets" /></div>
                                            <div :class="{ percent: true, negative: totals.assets < 0 }">100.00 %</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-liabilities-title">
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">負債</div>
                                            <div class="total">金額</div>
                                            <div class="percent">%</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-liabilities-items">
                                <!-- 流動負債 -->
                                <div class="group secondary" v-if="datas.currentLiabilities.length > 0">
                                    <div class="group-title">流動負債</div>
                                    <ul class="item-list">
                                        <li v-for="(item, index) in datas.currentLiabilities" :key="`${index}-${item.accno}`">
                                            <div class="accno">{{ item.accno }}</div>
                                            <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                            <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                            <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">流動負債總額</div>
                                            <div class="total"><Money :v="totals.currentLiabilities" /></div>
                                            <div :class="{ percent: true, negative: totals.currentLiabilities < 0 }">{{ getPercent(totals.currentLiabilities, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                                <!-- 非流動負債 -->
                                <div class="group secondary" v-if="datas.nonCurrentLiabilities.length > 0">
                                    <div class="group-title" style="margin-top: 20px">非流動負債</div>
                                    <ul class="item-list">
                                        <li v-for="(item, index) in datas.nonCurrentLiabilities" :key="`${index}-${item.accno}`">
                                            <div class="accno">{{ item.accno }}</div>
                                            <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                            <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                            <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">非流動負債總額</div>
                                            <div class="total"><Money :v="totals.nonCurrentLiabilities" /></div>
                                            <div :class="{ percent: true, negative: totals.nonCurrentLiabilities < 0 }">
                                                {{ getPercent(totals.nonCurrentLiabilities, totals.liabilitiesAndEquity) }} %
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">負債總額</div>
                                            <div class="total"><Money :v="totals.liabilities" /></div>
                                            <div :class="{ percent: true, negative: totals.liabilities < 0 }">{{ getPercent(totals.liabilities, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-equity-title">
                                <div class="block-title">權益</div>
                            </div>
                            <div class="cell-block cell-equity-items">
                                <!-- 權益 -->
                                <div class="group">
                                    <div class="group-title">股東權益</div>
                                    <ul class="item-list">
                                        <li v-for="(item, index) in datas.totalEquity" :key="`${index}-${item.accno}`">
                                            <div class="accno">{{ item.accno }}</div>
                                            <div class="name" @click="onClickItem(item)">{{ item.accnam }}</div>
                                            <div class="total"><Money :v="item.ntTotalAmount" /></div>
                                            <div :class="{ percent: true, negative: item.ntTotalAmount < 0 }">{{ getPercent(item.ntTotalAmount, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">權益總額</div>
                                            <div class="total"><Money :v="totals.totalEquity" /></div>
                                            <div :class="{ percent: true, negative: totals.totalEquity < 0 }">{{ getPercent(totals.totalEquity, totals.liabilitiesAndEquity) }} %</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div class="cell-block cell-liabilities-and-equity-total">
                                <div class="group">
                                    <ul class="item-list">
                                        <li class="sub-total">
                                            <div class="accno"></div>
                                            <div class="name">負債與權益總計</div>
                                            <div class="total" :class="{ unequal: !isAssetsAndLiabilitiesEqual }"><Money :v="totals.liabilitiesAndEquity" /></div>
                                            <div :class="{ percent: true, negative: totals.liabilitiesAndEquity < 0 }">100.00 %</div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div v-if="selectedProject.length > 0" style="font-size: 10pt; margin-bottom: 10px">
                            <div v-for="(project, index) in selectedProject" :key="project.poo">
                                <div>
                                    <span v-if="!index">專案：{{ project.nam }}</span>
                                    <span v-else style="margin-left: 3em">{{ project.nam }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="no-print">
                            <el-alert v-if="!isAssetsAndLiabilitiesEqual" type="error" :closable="false" style="padding-bottom: 12px">
                                <i class="el-icon-warning"></i> <strong>注意！資產總額與負債總額有差異，請做會計科目檢查。</strong>
                            </el-alert>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <DetailDialog
            :visible="detailDialog.visible"
            :StartDate="queryParams.startDate"
            :EndDate="queryParams.queryDate"
            :poo="queryParams.poo"
            :accno="detailDialog.accno"
            :accnam="detailDialog.accnam"
            @close="onDetailDialogClose"
        />
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';
import qs from 'qs';
import moment from 'moment';
import ahDropdownDatePicker from '@/components/ui/ahDropdownDatePicker.vue';
import SearchForm from '@/components/ui/SearchForm/SearchForm.vue';
import SearchFormItem from '@/components/ui/SearchForm/SearchFormItem.vue';

export default {
    name: 'ProjectAccountingBalanceSheet',
    mixins: [],
    props: {},
    components: {
        ahDropdownDatePicker,
        SearchForm,
        SearchFormItem,
        DetailDialog: () => import('@/components/ui/ahAccountingGeneralLedgerDialog.vue'),
    },
    data() {
        return {
            openDate: null,

            isLoading: false,
            isShowZeroItems: true,
            dateRange: [null, null],
            selectedProject: [],
            projectList: [],
            queryParams: {
                queryDate: null,
                startDate: null,
                poo: undefined,
            },
            datas: {
                currentAssets: [], // 流動資產
                nonCurrentAssets: [], // 非流動資產
                currentLiabilities: [], // 流動負債
                nonCurrentLiabilities: [], // 非流動負債
                totalEquity: [], // 權益
            },
            totals: {
                currentAssets: 0,
                nonCurrentAssets: 0,
                currentLiabilities: 0,
                nonCurrentLiabilities: 0,
                totalEquity: 0,
                assets: 0,
                liabilities: 0,
                liabilitiesAndEquity: 0,
            },

            detailDialog: {
                accno: null,
                accnam: null,
                visible: false,
            },
        };
    },
    computed: {
        ...mapGetters({ isTrying: 'menu/isTrying' }),
        companyName() {
            return this.$store.getters['menu/companyInfo'].cH_CMP;
        },
        queryDateText() {
            const START_DATE = moment(+this.startDate);
            const END_DATE = moment(this.queryParams.queryDate);

            return `民國 ${START_DATE.year() - 1911} 年 ${START_DATE.format('MM 月 DD 日')} ～ 民國 ${END_DATE.year() - 1911} 年 ${END_DATE.format('MM 月 DD 日')}`;
        },
        todayDateTxt() {
            const TODAY = moment();
            return `民國 ${TODAY.year() - 1911} 年 ${TODAY.format('MM 月 DD 日')}`;
        },
        startDate() {
            if (!this.queryParams.startDate) {
                return moment(this.queryParams.queryDate).startOf('year').format('x');
            }
            return moment(this.queryParams.startDate).format('x');
        },
        isAssetsAndLiabilitiesEqual() {
            return this.totals.liabilitiesAndEquity === this.totals.assets;
        },
    },
    async mounted() {
        this.dateRange = [parseInt(moment().format('x'), 10), null];
        this.queryParams.queryDate = +moment().endOf('day').format('x');
        this.updateData();
        this.fetchProjectList();
    },
    methods: {
        async fetchProjectList() {
            const resp = await this.$api({
                url: 'AccountingSummaryReport/GetProjectList',
                method: 'GET',
            });

            this.projectList = resp.data.items;
        },
        async updateData() {
            // this.updateQuery();

            // 先找出該年度的開帳傳票，才是當年的起始日期
            const year = moment(this.dateRange[0]).year();
            this.queryParams.startDate = await this.getYearlyOpenVoucher(year);
            this.queryParams.queryDate = +moment(this.dateRange[0]).endOf('day').format('x');

            // 阻擋查詢日期在開帳日期之前
            if (this.queryParams.startDate) {
                const OPEN_DATE = moment(+this.queryParams.startDate);
                const SELECTED_DATE = moment(+this.queryParams.queryDate);

                if (SELECTED_DATE.isBefore(OPEN_DATE)) {
                    this.$message({
                        showClose: true,
                        message: `${SELECTED_DATE.year()}年開帳日期為 ${OPEN_DATE.format('YYYY-MM-DD')}，請重新選擇日期範圍。`,
                        type: 'error',
                    });
                    return;
                }
            }

            try {
                this.isLoading = true;
                const resp = await this.$api({
                    url: 'AccountingSummaryReport/QueryBalanceSheet',
                    params: this.queryParams,
                    paramsSerializer: (params) => qs.stringify(params, { indices: false, skipNulls: true }),
                    method: 'GET',
                });

                console.log('QueryBalanceSheet datas:', resp.data);
                this.datas.currentAssets = _.map(resp.data.currentAssets, (o) => ({
                    ...o,
                    ntTotalAmount: o.dC_TYP === 1 ? o.ntTotalAmount : -o.ntTotalAmount,
                }));
                this.datas.nonCurrentAssets = _.map(resp.data.nonCurrentAssets, (o) => ({
                    ...o,
                    ntTotalAmount: o.dC_TYP === 1 ? o.ntTotalAmount : -o.ntTotalAmount,
                }));
                this.datas.currentLiabilities = _.map(resp.data.currentLiabilities, (o) => ({
                    ...o,
                    ntTotalAmount: o.dC_TYP === 2 ? o.ntTotalAmount : -o.ntTotalAmount,
                }));
                this.datas.nonCurrentLiabilities = _.map(resp.data.nonCurrentLiabilities, (o) => ({
                    ...o,
                    ntTotalAmount: o.dC_TYP === 2 ? o.ntTotalAmount : -o.ntTotalAmount,
                }));
                this.datas.totalEquity = _.map(resp.data.totalEquity, (o) => ({
                    ...o,
                    ntTotalAmount: o.dC_TYP === 2 ? o.ntTotalAmount : -o.ntTotalAmount,
                }));

                // 如果 isShowZeroItems 為 false，則過濾掉金額為 0 的項目
                if (!this.isShowZeroItems) {
                    this.datas.currentAssets = this.datas.currentAssets.filter((item) => item.ntTotalAmount !== 0);
                    this.datas.nonCurrentAssets = this.datas.nonCurrentAssets.filter((item) => item.ntTotalAmount !== 0);
                    this.datas.currentLiabilities = this.datas.currentLiabilities.filter((item) => item.ntTotalAmount !== 0);
                    this.datas.nonCurrentLiabilities = this.datas.nonCurrentLiabilities.filter((item) => item.ntTotalAmount !== 0);
                    this.datas.totalEquity = this.datas.totalEquity.filter((item) => item.ntTotalAmount !== 0);
                }

                // 計算分類小計
                this.totals.currentAssets = this.datas.currentAssets.reduce((a, b) => a + b.ntTotalAmount, 0);
                this.totals.nonCurrentAssets = this.datas.nonCurrentAssets.reduce((a, b) => a + b.ntTotalAmount, 0);
                this.totals.currentLiabilities = this.datas.currentLiabilities.reduce((a, b) => a + b.ntTotalAmount, 0);
                this.totals.nonCurrentLiabilities = this.datas.nonCurrentLiabilities.reduce((a, b) => a + b.ntTotalAmount, 0);
                this.totals.totalEquity = this.datas.totalEquity.reduce((a, b) => a + b.ntTotalAmount, 0);

                // 計算統計
                this.totals.assets = this.totals.currentAssets + this.totals.nonCurrentAssets;
                this.totals.liabilities = this.totals.currentLiabilities + this.totals.nonCurrentLiabilities;
                this.totals.liabilitiesAndEquity = this.totals.liabilities + this.totals.totalEquity;
            } finally {
                this.isLoading = false;
            }
        },
        updateQuery() {
            this.queryParams.startDate = this.openDate;
            this.queryParams.queryDate = Number(moment(Number(this.queryParams.queryDate)).endOf('day').format('x'));
        },
        disabledDate(date) {
            if (!this.openDate) return false;

            const DATE = moment(date);
            const FIRST_DATE_OF_THIS_YEAR = moment(+this.queryParams.StartDate).startOf('year');

            return DATE.isBefore(moment(this.openDate)) && DATE.isAfter(FIRST_DATE_OF_THIS_YEAR);
        },
        // 找出指定年份的開帳傳票，去定義開始日期
        async getYearlyOpenVoucher(year) {
            console.log('year', year);
            const resp = await this.$api({
                url: 'AccountingSummaryPage/QueryVouchers',
                method: 'GET',
                params: {
                    StartDateStart: moment(`${year}/01/01`, 'YYYY-MM-DD').startOf('year').format('x'),
                    StartDateEnd: moment(`${year}/12/31`, 'YYYY-MM-DD').endOf('year').format('x'),
                    TransactionType: 12,
                },
            });
            console.log('resp', resp);
            if (resp.data.totalNum) {
                return _.head(resp.data.items)?.dat;
            }
            return null;
        },
        getPercent(value, total) {
            return (Math.round((value / total) * 10000) / 100).toFixed(2);
        },
        onSearchClick() {
            this.updateData();
        },
        onPrintClick() {
            window.print();
        },

        onClickItem(item) {
            console.log('click', item);
            this.detailDialog.accno = item.accno;
            this.detailDialog.accnam = item.accnam;
            this.detailDialog.visible = true;
        },
        onDetailDialogClose(refresh) {
            this.detailDialog.visible = false;
            if (refresh) this.updateData();
        },
        onSelectProjectChange() {
            this.queryParams.poo = this.selectedProject.length ? this.selectedProject.map((o) => o.poo) : null;
            this.updateData();
        },
    },
};
</script>

<style lang="scss">
.project-accounting-balance-sheet {
    .action-bar {
        display: flex;
        align-items: center;
        justify-content: center;
    }
    @media print {
        .ah-panel.actions {
            display: none;
        }
        .doc-action {
            display: none;
        }
    }
    .doc-action {
        text-align: right;
    }
    .datepicker-label {
        display: inline-block;
        margin-right: 24px;
        font-size: 10pt;
        color: #888;
    }

    .panel-body.loading {
        opacity: 0.5;
        transition: 0.2s;
    }

    .doc-header {
        margin-top: 10px;
        text-align: center;
        padding: 20px 30px 0px 30px;

        @media print {
            margin-top: 30px;
            border-top: 0;
            padding: 0;
            line-height: 130%;
        }

        .company-name {
            font-size: 15pt;
            font-weight: bold;
            @media print {
                font-size: 11pt;
            }
        }
        .doc-name {
            font-size: 15pt;
            font-weight: bold;
            margin: 3px 0;

            @media print {
                margin: 2px 0;
                font-size: 11pt;
            }
        }
        .date {
            font-size: 12pt;
            font-weight: bold;
            @media print {
                font-size: 9pt;
            }
        }
    }

    .table-block {
        padding: 0 30px 30px 30px;
        @media print {
            padding: 0;
        }
    }

    .balance-sheet-table {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-template-rows: 35px auto 35px auto 35px;
        border: 1px solid #000;
        font-size: 12pt;
        line-height: 160%;
        margin-bottom: 50px;

        @media print {
            font-size: 8.5pt;
            padding-bottom: 0px;
        }

        .cell-block {
            border: 1px solid #000;
            padding: 5px 15px;
        }

        /* 資產標題 */
        .cell-assets-title {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 1;
            grid-row-end: 2;
        }
        /* 資產項目（流動＆非流動） */
        .cell-assets-items {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 2;
            grid-row-end: 5;
        }
        /* 資產總計 */
        .cell-assets-total {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 5;
            grid-row-end: 6;
        }
        /* 負債標題 */
        .cell-liabilities-title {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 1;
            grid-row-end: 2;
        }
        /* 負債項目（流動＆非流動） */
        .cell-liabilities-items {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 2;
            grid-row-end: 3;
        }
        /* 權益標題 */
        .cell-equity-title {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 3;
            grid-row-end: 4;
        }
        /* 權益項目 */
        .cell-equity-items {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 4;
            grid-row-end: 5;
        }
        /* 負債與權益總計 */
        .cell-liabilities-and-equity-total {
            grid-column-start: 2;
            grid-column-end: 3;
            grid-row-start: 5;
            grid-row-end: 6;
        }

        /* 標題 */
        .block-title {
            font-weight: bold;
        }
        /* 次標題 */
        .group {
            &.secondary {
                margin-left: 20px;
            }
            /* 分類標題 */
            .group-title {
                font-weight: bold;
            }
            /* 項目列表 */
            ul.item-list {
                display: table;
                width: 100%;
                margin-bottom: 20px;

                > li {
                    display: table-row;
                    > .accno {
                        display: table-cell;
                        display: none;
                        width: 120px;
                    }
                    > .name {
                        cursor: pointer;
                        display: table-cell;
                        padding-left: 30px;
                        @media print {
                            padding-left: 0.3cm;
                        }
                        &:hover {
                            color: #463cb2;
                        }
                    }
                    > .total {
                        display: table-cell;
                        text-align: right;
                        width: 200px;
                        @media print {
                            width: 2cm;
                        }

                        &.unequal {
                            color: red;
                        }

                        .money {
                            font-weight: normal;
                            &.negative {
                                color: red;
                            }
                        }
                    }
                    > .percent {
                        display: table-cell;
                        text-align: right;
                        width: 100px;
                        &.negative {
                            color: red;
                        }
                        @media print {
                            width: 1.7cm;
                        }
                    }
                }
                > li.sub-total {
                    > .name {
                        padding-left: 0px;
                        font-weight: bold;
                        cursor: auto;
                    }
                }
            }
        }
    }
}
</style>
